import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Customer, CreateCustomerRequest, UpdateCustomerRequest } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import PhoneInput from '../common/PhoneInput';
import { formatCustomerError } from '../../utils/customerErrorMessage';
import { AppDialogFormActions } from '../common/AppDialog';

interface CustomerFormProps {
  customer?: Customer | null;
  onSubmit: (data: CreateCustomerRequest | UpdateCustomerRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  customer,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    taxIdentificationNumber: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (customer) {
      setFormData({
        firstName: customer.firstName || '',
        lastName: customer.lastName || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        taxIdentificationNumber: customer.taxIdentificationNumber || '',
      });
    } else {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        taxIdentificationNumber: '',
      });
    }
    setErrors({});
    setSubmitError(null);
  }, [customer]);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.phone && !/^[+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/[\s\-()]/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      const submitData = Object.fromEntries(
        Object.entries(formData).filter(([field, value]) =>
          value.trim() !== '' || (Boolean(customer) && field === 'taxIdentificationNumber'))
      );

      await onSubmit(submitData);
    } catch (error: unknown) {
      setSubmitError(formatCustomerError(error, t('customers.errors.saveFailed'), t));
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submitError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 3, flex: 1 }}>
        {/* Remove duplicate title - it's already in DialogTitle */}
        
        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: isMobile ? 2 : 3 
          }}
        >
          <TextField
            inputProps={{ 'data-testid': 'customer-first-name' }}
            fullWidth
            label={t('customers.firstName')}
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange('firstName')}
            error={!!errors.firstName}
            helperText={errors.firstName}
            required
            disabled={isLoading}
          />

          <TextField
            inputProps={{ 'data-testid': 'customer-last-name' }}
            fullWidth
            label={t('customers.lastName')}
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange('lastName')}
            error={!!errors.lastName}
            helperText={errors.lastName}
            required
            disabled={isLoading}
          />

          <TextField
            inputProps={{ 'data-testid': 'customer-email' }}
            fullWidth
            label={t('common.email')}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            error={!!errors.email}
            helperText={errors.email}
            disabled={isLoading}
          />

          <PhoneInput
            value={formData.phone}
            onChange={(value) => {
              setFormData(prev => ({ ...prev, phone: value }));
              if (errors.phone) {
                setErrors(prev => ({ ...prev, phone: '' }));
              }
            }}
            label={t('common.phone')}
            error={!!errors.phone}
            helperText={errors.phone}
            disabled={isLoading}
            fullWidth
          />

          <TextField
            fullWidth
            label={t('customers.taxIdentificationNumber')}
            name="taxIdentificationNumber"
            value={formData.taxIdentificationNumber}
            onChange={handleInputChange('taxIdentificationNumber')}
            error={!!errors.taxIdentificationNumber}
            helperText={errors.taxIdentificationNumber || t('customers.taxIdentificationNumberOptional')}
            disabled={isLoading}
            inputProps={{ maxLength: 32, inputMode: 'numeric' }}
          />

          <TextField
            fullWidth
            label={t('common.address')}
            name="address"
            multiline
            rows={3}
            value={formData.address}
            onChange={handleInputChange('address')}
            error={!!errors.address}
            helperText={errors.address}
            disabled={isLoading}
          />
        </Box>

        <AppDialogFormActions>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={isLoading}
            size={isMobile ? "large" : "medium"}
            fullWidth={isMobile}
            sx={{
              minHeight: isMobile ? 48 : 'auto',
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            data-testid="customer-submit"
            variant="contained"
            disabled={isLoading}
            size={isMobile ? "large" : "medium"}
            fullWidth={isMobile}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
            sx={{
              minHeight: isMobile ? 48 : 'auto',
            }}
          >
            {isLoading ? t('customers.saving') : (customer ? t('customers.update') : t('customers.create'))}
          </Button>
        </AppDialogFormActions>
      </Box>
    </Box>
  );
};

export default CustomerForm;
